import { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useSelector } from 'react-redux'
import { Route, Switch, Redirect, useLocation, useHistory } from 'react-router-dom'
import { RootState } from '@redux/store'
import { FeatureDict, selectFeatureIncludes } from '@authed/selectors'
import { useAppSelector } from '@redux/hooks'
import { AdminContext } from './admin.context'
import { NoMatchAndRefresh } from '../authed/no-match-and-refresh'
import { PermissionedRoute } from '../authed/permissioned.route'
import { Profile } from '../profile'
import { Accounts } from './integrations/accounts'
import { Targets } from './integrations/targets/targets'
import { UserActivityLog } from './organization/activities'
import { AdminReports } from './organization/reports'
import { EditPattern } from './template-elements/edit-pattern'
import { Fields } from './template-elements/fields'
import { Fieldsets } from './template-elements/fieldsets'
import { Lists } from './template-elements/lists'
import { ListsManager } from './template-elements/lists-manager'
import { Patterns } from './template-elements/patterns'
import { EditTemplate } from './templates/edit-template'
import { TemplateList } from './templates/list'
import { MainTabHolder } from '../components/tab-layouts/main-tab-holder'
import { MainTabsArea } from '../components/tab-layouts/main-tabs-area'
import { OrganizationSettings } from '../profile/organizationSettings'
import { EditTarget } from './integrations/targets/edit-target'

// sub routes
import { ListsManagerEdit } from './template-elements/lists-manager/edit'
import { ListsManagerNew } from './template-elements/lists-manager/new'
import { FolderList } from './templates/folders/folders'
import { GroupList } from './user-management/groups/groups'
import { Users } from './user-management/users/users'

export const Admin = () => {
  const [reffer, setReffer] = useState(useRef(null))
  const location = useLocation()
  const history = useHistory()

  const [USER_ACTIVITY_LOG = false] = useAppSelector(
    selectFeatureIncludes([FeatureDict.USER_ACTIVITY_LOG]),
  )

  function handleMenuRef() {
    setReffer({ ...reffer })
  }

  if (location.pathname.includes('connectors')) {
    history.replace('/admin/accounts')
  }

  // $root_url/admin/connectors/?oauth_success=true&connector_id=$connector_id

  // this has to be done out here because hooks will fire invariant if it isn't done every time.
  const firstAccessibleRoute = useSelector((state: RootState) => {
    let route = '/admin/settings'
    if (state.Auth.permissions.all_privileges || state.Auth.permissions.access_reports) {
      route = '/admin/account/reports'
    } else if (state.Auth.permissions.manage_users) {
      route = '/admin/users'
    } else if (state.Auth.permissions.manage_groups) {
      route = '/admin/activities'
    } else if (state.Auth.permissions.manage_connectors) {
      route = '/admin/accounts'
    } else if (state.Auth.permissions.manage_targets) {
      route = '/admin/targets'
    } else if (state.Auth.permissions.manage_patterns) {
      route = '/admin/patterns'
    } else if (state.Auth.permissions.manage_field_sets) {
      route = '/admin/fieldsets'
    } else if (state.Auth.permissions.manage_lists) {
      route = '/admin/lists'
    } else if (state.Auth.permissions.manage_templates) {
      route = '/admin/templates'
    } else if (state.Auth.permissions.manage_folders) {
      route = '/admin/folders'
    }
    return route
  })

  if (location.pathname === '/admin') {
    // maybe animate prescence problem?
    return <Redirect to={firstAccessibleRoute} />
  }

  return (
    <motion.div
      className="admin"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.15 } }}
      exit={{ opacity: 0, transition: { duration: 0.15 } }}
    >
      <AdminContext.Provider
        value={{
          menuRef: reffer,
          setMenuRef: handleMenuRef,
        }}
      >
        <MainTabHolder>
          <MainTabsArea>
            <AnimatePresence exitBeforeEnter initial={false}>
              <Switch location={location}>
                <PermissionedRoute path="/admin/account/reports" component={AdminReports} />
                <PermissionedRoute path="/admin/users" component={Users} />
                {USER_ACTIVITY_LOG && (
                  <PermissionedRoute path="/admin/activities" component={UserActivityLog} />
                )}
                <PermissionedRoute path="/admin/groups" component={GroupList} />
                <PermissionedRoute path="/admin/accounts" component={Accounts} />
                <PermissionedRoute path="/admin/connectors" component={Accounts} />
                <PermissionedRoute path="/admin/targets/:targetId" component={EditTarget} />
                <PermissionedRoute path="/admin/targets" component={Targets} />
                <PermissionedRoute path="/admin/patterns/:id" component={EditPattern} />
                <PermissionedRoute path="/admin/patterns" component={Patterns} />
                <PermissionedRoute path="/admin/lists" component={Lists} />
                <PermissionedRoute
                  path="/admin/lists-manager/edit/:id"
                  component={ListsManagerEdit}
                />
                <PermissionedRoute path="/admin/lists-manager/new" component={ListsManagerNew} />
                <PermissionedRoute path="/admin/lists-manager" component={ListsManager} />
                <PermissionedRoute path="/admin/fieldsets/:fieldSetId/fields" component={Fields} />
                <PermissionedRoute path="/admin/fieldsets" component={Fieldsets} />
                <PermissionedRoute exact path="/admin/settings" component={Profile} />
                <PermissionedRoute
                  path="/admin/templates/edit-template/:id"
                  component={EditTemplate}
                />
                <PermissionedRoute exact path="/admin/templates" component={TemplateList} />
                <PermissionedRoute path="/admin/folders" component={FolderList} />
                <PermissionedRoute
                  path="/admin/account/settings"
                  component={OrganizationSettings}
                />
                <Route component={NoMatchAndRefresh} />
              </Switch>
            </AnimatePresence>
          </MainTabsArea>
        </MainTabHolder>
      </AdminContext.Provider>
    </motion.div>
  )
}
